import { useEffect, useState, useRef, useCallback } from "react";
import { socket } from "../../../common/socket";

export const useSocketConnection = (groupChats, selectedChatId) => {
  const [isSocketConnected, setIsSocketConnected] = useState(socket.connected);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const joinedRoomsRef = useRef(new Set());
  const pendingJoinsRef = useRef(new Set());

  const joinRoom = useCallback((chatId) => {
    if (!chatId) return;
    const id = String(chatId);
    
    // Already joined or pending
    if (joinedRoomsRef.current.has(id)) return;
    
    // If not connected/authenticated, queue for later
    if (!socket.connected || !isAuthenticated) {
      pendingJoinsRef.current.add(id);
      return;
    }

    console.log("🔗 Joining chat room:", id);
    socket.emit("joinChat", { chatId: id });
    joinedRoomsRef.current.add(id);
    pendingJoinsRef.current.delete(id);
  }, [isAuthenticated]);

  const joinAllRooms = useCallback(() => {
    // Join pending rooms first
    pendingJoinsRef.current.forEach(id => {
      if (!joinedRoomsRef.current.has(id)) {
        console.log("🔗 Joining pending room:", id);
        socket.emit("joinChat", { chatId: id });
        joinedRoomsRef.current.add(id);
      }
    });
    pendingJoinsRef.current.clear();

    // Join group chat rooms
    if (Array.isArray(groupChats)) {
      groupChats.forEach((chat) => {
        const cid = chat?.chatRequestId || chat?._id || chat?.groupId;
        if (cid && !joinedRoomsRef.current.has(String(cid))) {
          joinRoom(cid);
        }
      });
    }

    // Join selected chat room
    if (selectedChatId && !joinedRoomsRef.current.has(String(selectedChatId))) {
      joinRoom(selectedChatId);
    }
  }, [groupChats, selectedChatId, joinRoom]);

  useEffect(() => {
    const onConnect = () => {
      console.log("✅ Socket connected");
      setIsSocketConnected(true);
    };

    const onAuthenticated = (data) => {
      console.log("✅ Socket authenticated:", data);
      setIsAuthenticated(true);
      // Now safe to join rooms
      setTimeout(() => joinAllRooms(), 100);
    };

    const onDisconnect = (reason) => {
      console.log("❌ Socket disconnected:", reason);
      setIsSocketConnected(false);
      setIsAuthenticated(false);
      joinedRoomsRef.current.clear();
    };

    const onError = (error) => {
      console.error("❌ Socket error:", error);
      if (error?.message?.includes("Authentication")) {
        setIsAuthenticated(false);
      }
    };

    socket.on("connect", onConnect);
    socket.on("connected", onAuthenticated); // Backend sends this after auth
    socket.on("disconnect", onDisconnect);
    socket.on("error", onError);

    // Initial state check
    if (socket.connected) {
      setIsSocketConnected(true);
      // Emit a ping to check if authenticated
      socket.emit("connected", { timestamp: Date.now() });
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off("connected", onAuthenticated);
      socket.off("disconnect", onDisconnect);
      socket.off("error", onError);
    };
  }, [joinAllRooms]);

  // Re-join rooms when authenticated or chats change
  useEffect(() => {
    if (isSocketConnected && isAuthenticated) {
      joinAllRooms();
    }
  }, [isSocketConnected, isAuthenticated, joinAllRooms]);

  // Join selected chat immediately when it changes
  useEffect(() => {
    if (selectedChatId && isSocketConnected && isAuthenticated) {
      joinRoom(selectedChatId);
    }
  }, [selectedChatId, isSocketConnected, isAuthenticated, joinRoom]);

  return { isSocketConnected, isAuthenticated, joinedRoomsRef, joinRoom };
};